import React, { useState } from "react";
import { classNames } from "../helpers/classNames";
import Image from "../components/elements/Image";
import noEmpImg from "../assets/images/noEmpImg.svg";
import noUserDetails from "../assets/images/userDetails.svg";
import { useSsoChart } from "../Context/ChartSsoContext";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import {
  NINEBOX_API_URL,
  NINEBOX_BASE_URL,
  SUCCESSION_API_URL,
  SUCCESSION_BASE_URL,
} from "../config/host";

const OrgNodeActionButton = ({ title, icon, click = () => { } }) => {
  return (
    <div className="relative group">
      <button
        type="button"
        className="w-5 aspect-square flex items-center justify-center border text-xxs rounded border-gray-400 text-black hover:bg-black hover:text-white transition-all duration-200"
        onClick={click}
      >
        {icon && icon}
      </button>
      {title && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 flex items-center justify-center w-40 z-10 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
          <div className="bg-black py-1 px-2 rounded mb-1 relative before:content-[''] before:absolute before:bottom-0.5 before:left-1/2 before:-translate-x-1/2 before:w-1.5 before:aspect-square before:bg-black before:z-[-1] before:rotate-45">
            <div className="text-white text-center text-xxs">{title}</div>
          </div>
        </div>
      )}
    </div>
  );
};

const OrgNode = ({
  node,
  onAdd = () => { },
  onEdit = () => { },
  setDeleteModal = () => { },
  setAssignModal = () => { },
  addAboveNode = () => { },
  setDetails = () => { },
  setSyncDataModalOpen = () => { },
  setSelectedSite = () => { },
  getGenarateToken = () => { },
  onClick = () => { },
  highlighted,
  isUnassigned,
  activeId,
  setActiveId,
}) => {
  const isActive = activeId === node._id;
  const [isHovered, setHovered] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
  } = useDraggable({ id: node._id });
  const { isOver, setNodeRef: setDropRef } = useDroppable({ id: node._id });

  const headerColor = node?.userDetails
    ? Array.isArray(node.userDetails.departmentDetails) &&
      node.userDetails.departmentDetails.length > 0
      ? node.userDetails.departmentDetails[0]?.theme?.headerColor
      : "#71b483"
    : node?.parentDepartment
      ? (Array.isArray(node.parentDepartment) &&
        node.parentDepartment.length > 0 &&
        node.parentDepartment[0]?.theme?.headerColor) ||
      "#f23434"
      : "#f23434";

  return (
    <>
      {isUnassigned && !node?.userId && (
        <div className="w-[calc(100%+10px)] h-[calc(100%+6px)] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 origin-center absolute rounded-[18px] overflow-hidden bg-[#ffe600] z-[-1] -mt-0.5" style={{ filter: "drop-shadow(0px 0px 10px rgba(255, 230, 0, 1))" }}>
          <svg className="w-full h-full">
            <rect x="4" y="4" rx="18" ry="18" width="226" height="110"
              className="draw-path"
              stroke="rgba(0,0,0,0.1)"
              strokeWidth={10}
              fill="transparent"
            />
          </svg>
        </div>
      )}
      {((node?.userId && highlighted === node?.userId) || isHovered) && (
        <div className="w-[calc(100%+10px)] h-[calc(100%+6px)] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 origin-center absolute rounded-[18px] overflow-hidden bg-[#ffe600] z-[-2] -mt-0.5" />
      )}
      <div
        className={classNames(
          "w-[220px] h-[104px] rounded-2xl shadow flex flex-col relative z-[1]"
        )}
        style={{
          backgroundColor: headerColor,
        }}
      >
        <div className="h-8 flex items-center justify-center text-center text-xs font-semibold flex-shrink-0 text-white theme-bg rounded-t-2xl relative" onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
          <div className="absolute top-1/2 left-2 -translate-y-1/2">
            {node?.parentId && (
              <OrgNodeActionButton
                title={"Add Parent"}
                icon={<i className="fa-regular fa-plus"></i>}
                click={(e) => {
                  e.stopPropagation();

                  addAboveNode(node);
                }}
                designAlt={true}
              />
            )}
          </div>
          <div className="px-10">
            {node?.userDetails
              ? node?.userDetails?.departmentDetails?.length > 0 &&
                node?.userDetails?.departmentDetails[0]?.name
                ? `${node.userDetails.departmentDetails[0].name.substring(
                  0,
                  25
                )}${node.userDetails.departmentDetails[0].name.length > 25
                  ? "..."
                  : ""
                }`
                : "Department"
              : "Unassigned Card"}
          </div>
        </div>
        <div className="bg-white flex-shrink flex-grow py-2 px-2 rounded-b-2xl" onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
          <div className="flex items-start w-full gap-2">
            <div
              className="w-12 h-12 rounded-xl bg-center bg-cover border-2 flex-shrink-0 theme-border"
              onClick={(e) => {
                e.stopPropagation();
                onClick();
              }}
            >
              <Image
                src={
                  node?.userDetails
                    ? node.userDetails.image?.url || noEmpImg
                    : noUserDetails
                }
                alt="User profile"
              />
            </div>
            <div className="flex-shrink flex-grow space-y-2">
              <div className="">
                <div className="font-semibold text-xs">
                  {node?.userDetails
                    ? node?.userDetails?.firstName
                      ? `${node?.userDetails?.firstName} ${node?.userDetails?.lastName}`.substring(
                        0,
                        18
                      ) +
                      (`${node?.userDetails?.firstName} ${node?.userDetails?.lastName} `
                        .length > 18
                        ? "..."
                        : "")
                      : "Employee"
                    : "Assign New Employee"}{" "}
                  {node?.userDetails?._id && node?.cardNumber !== 1
                    ? `(${node?.cardNumber})`
                    : ""}
                </div>
                <div className="text-xxs opacity-50 font-medium">
                  {node.userDetails?.positionDetails?.title || "Job title"}
                </div>
              </div>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center space-x-1">
                  <OrgNodeActionButton
                    title={"View Details"}
                    icon={<i className="fa-regular fa-info"></i>}
                    click={(e) => {
                      e.stopPropagation();
                      onClick();
                    }}
                  />
                  {node?.userDetails?._id && (
                    <OrgNodeActionButton
                      title={"Clear Position"}
                      icon={<i className="fa-regular fa-user-xmark"></i>}
                      click={(e) => {
                        e.stopPropagation();
                        setAssignModal({
                          isOpen: true,
                          _id: node?._id,
                          name: `${node?.userDetails?.firstName} ${node?.userDetails?.lastName}`,
                        });
                      }}
                    />
                  )}
                  {node?.parentId && (
                    <OrgNodeActionButton
                      title={"Remove from OrgChart"}
                      icon={<i className="fa-regular fa-trash-alt"></i>}
                      click={(e) => {
                        e.stopPropagation();
                        setDeleteModal({ isOpen: true, data: node });
                      }}
                    />
                  )}
                  {node?.userDetails?._id && (
                    <OrgNodeActionButton
                      title={"View in 9BoxNow"}
                      icon={
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 25 25"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M7.29165 1.33929V5.80357C7.29165 6.54325 6.76699 7.14286 6.11978 7.14286H1.17188C0.524658 7.14286 0 6.54325 0 5.80357V1.33929C0 0.599609 0.524658 0 1.17188 0H6.11978C6.76699 0 7.29165 0.599609 7.29165 1.33929ZM16.1458 14.7321V10.2679C16.1458 9.52818 15.6212 8.92857 14.974 8.92857H10.026C9.37881 8.92857 8.85415 9.52818 8.85415 10.2679V14.7321C8.85415 15.4718 9.37881 16.0714 10.026 16.0714H14.9739C15.6212 16.0714 16.1458 15.4718 16.1458 14.7321ZM17.7083 1.33929V5.80357C17.7083 6.54325 18.233 7.14286 18.8802 7.14286H23.8281C24.4753 7.14286 25 6.54325 25 5.80357V1.33929C25 0.599609 24.4753 0 23.8281 0H18.8802C18.233 0 17.7083 0.599609 17.7083 1.33929ZM16.1458 5.80357V1.33929C16.1458 0.599609 15.6212 0 14.974 0H10.026C9.37881 0 8.85415 0.599609 8.85415 1.33929V5.80357C8.85415 6.54325 9.37881 7.14286 10.026 7.14286H14.9739C15.6212 7.14286 16.1458 6.54325 16.1458 5.80357ZM6.11978 8.92857H1.17188C0.524658 8.92857 0 9.52818 0 10.2679V14.7321C0 15.4718 0.524658 16.0714 1.17188 16.0714H6.11978C6.76699 16.0714 7.29165 15.4718 7.29165 14.7321V10.2679C7.29165 9.52818 6.76699 8.92857 6.11978 8.92857ZM0 19.1964V23.6607C0 24.4004 0.524658 25 1.17188 25H6.11978C6.76699 25 7.29165 24.4004 7.29165 23.6607V19.1964C7.29165 18.4568 6.76699 17.8571 6.11978 17.8571H1.17188C0.524658 17.8571 0 18.4568 0 19.1964ZM18.8802 16.0714H23.8281C24.4753 16.0714 25 15.4718 25 14.7321V10.2679C25 9.52818 24.4753 8.92857 23.8281 8.92857H18.8802C18.233 8.92857 17.7083 9.52818 17.7083 10.2679V14.7321C17.7083 15.4718 18.233 16.0714 18.8802 16.0714ZM18.8802 25H23.8281C24.4753 25 25 24.4004 25 23.6607V19.1964C25 18.4568 24.4753 17.8571 23.8281 17.8571H18.8802C18.233 17.8571 17.7083 18.4568 17.7083 19.1964V23.6607C17.7083 24.4004 18.233 25 18.8802 25ZM8.85415 19.1964V23.6607C8.85415 24.4004 9.37881 25 10.026 25H14.9739C15.6211 25 16.1458 24.4004 16.1458 23.6607V19.1964C16.1458 18.4568 15.6211 17.8571 14.9739 17.8571H10.026C9.37881 17.8571 8.85415 18.4568 8.85415 19.1964Z"
                            fill="#0CCEB0"
                          />
                        </svg>
                      }
                      click={(e) => {
                        e.stopPropagation();
                        setSyncDataModalOpen(true);
                        setDetails(node);
                        setSelectedSite("nineBoxNow");
                        getGenarateToken(NINEBOX_BASE_URL, NINEBOX_API_URL);
                      }}
                    />
                  )}
                  {node?.userDetails?._id && (
                    <OrgNodeActionButton
                      title={"View in SuccessionNow"}
                      icon={
                        <svg
                          viewBox="0 0 513 513"
                          width="16"
                          height="16"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fill="#14B8A6"
                            d="M1.000002,280.444153 C1.223670,280.180145 1.447338,280.360321 1.671003,280.540466 C5.386623,273.630829 11.803145,273.868317 18.374180,273.950195 C31.864016,274.118256 45.357410,274.000641 58.849342,274.000671 C71.480461,274.000702 73.281357,276.028046 75.086731,288.312927 C78.203133,309.518860 85.805412,329.179047 96.249306,347.904388 C104.568893,362.820984 114.809128,376.284607 126.871872,388.104736 C145.854965,406.705963 167.712433,421.114075 193.007477,430.154358 C209.752991,436.139069 226.791946,439.719391 244.529373,441.131256 C280.600311,444.002441 314.104034,435.784058 345.707428,418.973022 C358.876068,411.968109 370.696991,402.992676 381.774933,392.879730 C406.188324,370.592834 423.349304,343.773468 433.144989,312.332977 C436.182678,302.583282 437.921417,292.410736 439.932373,282.367249 C440.871857,277.675171 444.032715,274.115326 449.331329,274.067413 C467.152527,273.906219 484.976135,273.985016 502.798737,274.021332 C507.462433,274.030853 510.038544,277.262573 512.731140,280.317261 C513.000000,282.370575 513.000000,284.741119 512.598450,287.370483 C511.441803,292.077454 510.804993,296.549500 509.911713,300.969757 C505.343475,323.574860 499.058228,345.634857 489.060669,366.522736 C480.265228,384.899017 470.140533,402.522278 456.555145,417.738770 C445.002563,430.678375 433.214966,443.612671 420.130493,454.929688 C402.785309,469.931885 383.161194,481.779297 362.024323,491.091034 C339.339111,501.084930 315.910034,508.067993 291.294678,511.079865 C290.463898,511.181519 289.761963,512.336182 289.000000,513.000000 C267.979095,513.000000 246.958206,513.000000 225.650543,512.600952 C224.520996,511.804535 223.715500,511.203766 222.829681,511.041473 C214.167664,509.454437 205.401230,508.330261 196.838348,506.330109 C174.461838,501.103241 153.276627,492.776398 133.097260,481.678741 C116.515808,472.559723 100.818481,462.091248 87.097000,449.241516 C74.697121,437.629456 63.198601,424.888031 52.478260,411.689728 C37.647896,393.431305 26.592037,372.730225 18.112982,350.802612 C10.506373,331.131256 4.939464,310.868073 2.903705,289.758636 C2.841846,289.117218 1.659791,288.583771 0.999998,288.000000 C1.000000,285.629425 1.000000,283.258881 1.000002,280.444153 z"
                          />
                          <path
                            fill="#5EEAD4"
                            d="M513.000000,233.555847 C512.646423,233.904068 512.012451,233.666321 511.979065,233.732330 C508.948853,239.723221 503.586975,240.114059 497.764465,240.050903 C483.436737,239.895538 469.106232,240.000046 454.776825,240.000076 C442.584473,240.000107 440.667755,237.980911 438.939484,225.907059 C435.779724,203.832382 427.976685,183.328644 416.626404,164.203156 C412.831024,157.807831 408.342407,151.825836 404.223572,145.619812 C401.825684,142.006851 402.439789,135.259201 406.048096,131.571564 C417.698975,119.664604 429.397034,107.794304 441.400116,96.245850 C446.820557,91.030731 453.316772,90.810326 459.285187,98.782249 C478.921661,125.010269 494.935913,153.146271 502.954956,185.193069 C506.212067,198.209778 508.361206,211.503174 511.071686,224.658875 C511.221924,225.388184 511.897278,226.009308 512.664062,226.340393 C513.000000,228.370560 513.000000,230.741104 513.000000,233.555847 z"
                          />
                          <path
                            fill="#2DD4BF"
                            d="M233.555832,1.000001 C233.815125,1.218541 233.630264,1.437081 233.445389,1.655620 C240.601547,5.478938 240.107498,12.149338 240.040039,18.821024 C239.905258,32.151508 239.871658,45.485901 240.036163,58.815716 C240.201141,72.182686 237.772903,73.234291 226.672913,74.873276 C206.310089,77.879997 187.275620,84.672279 169.305115,94.421669 C161.462784,98.676300 154.085632,103.830818 146.708649,108.885246 C142.123108,112.027084 136.486450,111.959572 132.537170,108.013283 C120.278389,95.763779 108.020287,83.513596 95.772232,71.253372 C91.792213,67.269386 91.349701,61.256947 95.711334,57.969135 C106.253693,50.022282 116.849335,42.038456 128.079697,35.137413 C144.607346,24.981203 162.329727,17.019445 181.124313,12.090422 C195.246399,8.386790 209.710358,5.989010 224.011856,2.961398 C224.906631,2.771973 225.711975,2.160063 226.279465,1.372356 C228.370560,1.000000 230.741104,1.000000 233.555832,1.000001 z"
                          />
                          <path
                            fill="#475569"
                            d="M306.335693,192.665619 C314.015472,202.592789 318.289032,213.385956 319.044403,225.649429 C320.040192,241.721268 314.933167,256.213501 304.097687,268.501099 C290.339935,284.147827 273.367188,293.411530 252.509613,294.856689 C244.724701,295.403564 236.944046,295.303162 229.172592,293.857178 C200.091354,288.537750 179.104462,266.146698 175.049911,236.752136 C170.245239,202.163681 195.495743,172.551025 225.561172,165.278091 C255.185684,158.123077 283.232880,166.368942 306.335693,192.665619 z"
                          />
                        </svg>
                      }
                      click={(e) => {
                        e.stopPropagation();
                        setSyncDataModalOpen(true);
                        setDetails(node);
                        setSelectedSite("successionNow");
                        getGenarateToken(SUCCESSION_BASE_URL, SUCCESSION_API_URL);
                      }}
                    />
                  )}
                </div>
                <div className="flex items-center space-x-1">
                  <OrgNodeActionButton
                    title={"Add Child"}
                    icon={<i className="a-regular fa-plus"></i>}
                    click={() => onAdd(node, "parent")}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrgNode;
